// Reading a class-note back into the "Đánh giá tiết học" form. Split out of the editor so the
// round-trip - the part that silently duplicated text - is testable without JSX.

import type { QltClassNoteData } from '../../api/types';

export interface ClassMarkBand {
  id: string;
  badge: string;
  label: string;
  mark: number;
}

// MOCK M5: no endpoint serves the band catalog and class-note.data.classMark has no enum.
export const CLASS_MARK_BANDS: ClassMarkBand[] = [
  { id: 'excellent', badge: '10-9', label: 'Rất tốt', mark: 10 },
  { id: 'good', badge: '9-8', label: 'Tốt', mark: 9 },
  { id: 'fair', badge: '8-7', label: 'Cần cố gắng', mark: 8 },
  { id: 'weak', badge: '7-6', label: 'Chưa tốt', mark: 7 },
  { id: 'poor', badge: '6-5', label: 'Kém', mark: 6 },
  { id: 'bad', badge: 'Dưới 5', label: 'Rất kém', mark: 4 },
];

// MOCK M5: verbatim from the "Nhận xét lớp" export - the improvement list, for 8-7 and below.
export const CLASS_COMMENT_IMPROVE = [
  'Mất tập trung',
  'Chưa hoàn thành nhiệm vụ',
  'Không tham gia',
  'Nề nếp kém',
  'Thiếu động lực',
  'Khó khăn trong việc quản lý thời gian',
];

// MOCK M5: the export and lms-fe have no positive list; 'Hăng hái phát biểu' is lms-fe's, the rest mirror the list above.
export const CLASS_COMMENT_POSITIVE = [
  'Tập trung nghe giảng',
  'Hoàn thành tốt nhiệm vụ',
  'Hăng hái phát biểu',
  'Tích cực tham gia',
  'Nề nếp tốt',
];

/** Every preset, for reading back a flat classComment whatever band it was written under. */
export const CLASS_COMMENT_PRESETS = CLASS_COMMENT_POSITIVE.concat(CLASS_COMMENT_IMPROVE);

const POSITIVE_BANDS = ['excellent', 'good'];

/** The comment chips a band offers; with no band picked yet, all of them. */
export function commentPresetsFor(bandId: string): string[] {
  if (!bandById(bandId)) return CLASS_COMMENT_PRESETS;
  return POSITIVE_BANDS.indexOf(bandId) >= 0 ? CLASS_COMMENT_POSITIVE : CLASS_COMMENT_IMPROVE;
}

/** Ticked comments that still fit a newly picked band; the rest are unticked. */
export function tagsForBand(tags: string[], bandId: string): string[] {
  const allowed = commentPresetsFor(bandId);
  return tags.filter((tag) => allowed.indexOf(tag) >= 0);
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
}

export function bandById(id: string): ClassMarkBand | undefined {
  for (let i = 0; i < CLASS_MARK_BANDS.length; i += 1) if (CLASS_MARK_BANDS[i].id === id) return CLASS_MARK_BANDS[i];
  return undefined;
}

// band id, then the badge seeded records store instead of the id, then the numeric mark it lands in
export function bandIdOf(data?: QltClassNoteData): string {
  if (!data) return '';
  const stored = asText(data.classMarkBand);
  for (let i = 0; i < CLASS_MARK_BANDS.length; i += 1) if (CLASS_MARK_BANDS[i].id === stored || CLASS_MARK_BANDS[i].badge === stored) return CLASS_MARK_BANDS[i].id;
  const mark = data.classMark;
  if (typeof mark !== 'number') return '';
  for (let i = 0; i < CLASS_MARK_BANDS.length; i += 1) if (mark >= CLASS_MARK_BANDS[i].mark) return CLASS_MARK_BANDS[i].id;
  return CLASS_MARK_BANDS[CLASS_MARK_BANDS.length - 1].id;
}

export function classMarkLabel(data?: QltClassNoteData): string {
  const band = bandById(bandIdOf(data));
  return band ? band.label : '';
}

/** Splits a record into ticked presets and leftover free text. Without this the already-joined
    classComment lands in the note box whole and every save re-appends the presets inside it. */
export function hydrateComment(data?: QltClassNoteData): { tags: string[]; note: string } {
  if (!data) return { tags: [], note: '' };
  if (Array.isArray(data.classCommentTags)) return { tags: asList(data.classCommentTags), note: asText(data.classCommentNote) };
  const tags: string[] = [];
  const rest: string[] = [];
  asText(data.classComment).split(';').forEach((part) => {
    const value = part.trim();
    if (!value) return;
    if (CLASS_COMMENT_PRESETS.indexOf(value) >= 0) tags.push(value); else rest.push(value);
  });
  return { tags, note: rest.join('; ') };
}

/** What the editor writes back. classComment stays the flat readable string every reader understands. */
export function commentPayload(tags: string[], note: string): Pick<QltClassNoteData, 'classComment' | 'classCommentTags' | 'classCommentNote'> {
  const trimmed = note.trim();
  return {
    classComment: tags.concat(trimmed ? [trimmed] : []).join('; '),
    classCommentTags: tags,
    classCommentNote: trimmed,
  };
}
